/**
 * GLI OBBLIGHI DI UNA PERSONA, E CHI È IN RITARDO (task 1921, BE 08-10-2026 §6).
 *
 * - `GET /api/obligations/me` — i miei: tutti, anche quelli in regola e non ancora dovuti. **204**
 *   se l'utente non ha un'anagrafica: per noi «niente da fare», non un errore.
 * - `GET /api/obligations?state=overdue&…` — per il responsabile e l'ufficio del personale: il
 *   perimetro è quello di `planning-compliance` (chi ha `PlanningEntry.readAll` vede l'azienda,
 *   gli altri il proprio sottoalbero), e restano solo le persone con almeno un obbligo che passa
 *   il filtro.
 * - `POST /api/obligations/confirm` / `DELETE …` — «per questo periodo non chiedo niente», solo
 *   sugli obblighi `confirmable` (smart working in modo `confirm_period`).
 *
 * Risposte semplici, non JSON:API.
 */
import { httpRequest, httpRequestStatus } from '@/shared/api/client';
import type { ObligationKind } from './obligationRules';

export type ObligationState = 'not_due' | 'at_risk' | 'met' | 'overdue';

export interface ObligationItem {
  kind: ObligationKind;
  ruleId: string;
  ruleCode?: string;
  label?: string;
  periodFrom?: string;
  periodTo?: string;
  dueAt?: string;
  overdueFrom?: string;
  state: ObligationState;
  overdueBehaviour?: string;
  notifyDaysBefore?: number[];
  progress?: { required?: number; done?: number; unit?: string };
  confirmable?: boolean;
  confirmedAt?: string | null;
  /** Con `overdueBehaviour: lock`, da `overdueFrom` in poi: il periodo non si pianifica più (BE 09-10-2026). */
  locked?: boolean;
  lockedSince?: string | null;
  missingDays?: unknown[];
}

export interface EmployeeObligations {
  employeeId: string;
  /** `long_absence` | `not_in_service` | null. Con un motivo, nessun obbligo. */
  exemptReason?: string | null;
  items: ObligationItem[];
}

export interface ObligationCounts { overdue?: number; atRisk?: number; notDue?: number; met?: number }

export const OBLIGATION_STATE: Record<ObligationState, { label: string; color: string }> = {
  overdue: { label: 'In ritardo', color: 'red' },
  at_risk: { label: 'In scadenza', color: 'yellow' },
  not_due: { label: 'Non ancora', color: 'gray' },
  met: { label: 'In regola', color: 'green' },
};

/** Dove si va per mettersi in regola. */
export const OBLIGATION_ROUTE: Record<ObligationKind, string> = {
  planning: '/planning',
  smart_working: '/smart-working',
  leave: '/leave',
  expense_report: '/expense-reports',
};

const UNIT: Record<string, [string, string]> = {
  working_days: ['giornata', 'giornate'],
  days: ['giorno', 'giorni'],
  office_days: ['giorno in sede', 'giorni in sede'],
  months: ['mese', 'mesi'],
};

/** «3 giorni su 10», «2 mesi aperti»; `null` quando il numero non dice niente (la conferma). */
export function progressLine(item: ObligationItem): string | null {
  const p = item.progress;
  if (!p || p.unit === 'confirmation' || p.required == null) return null;
  const words = UNIT[p.unit ?? ''] ?? ['', ''];
  if (p.unit === 'months') {
    const n = p.required;
    return `${n} ${n === 1 ? 'mese' : 'mesi'} da inviare`;
  }
  return `${p.done ?? 0} ${p.required === 1 ? words[0] : words[1]} su ${p.required}`.trim();
}

/** Gli aperti della home: tutto quel che non è in regola, i ritardi prima, poi per scadenza. */
export function openItems(o: EmployeeObligations | null | undefined): ObligationItem[] {
  if (!o || o.exemptReason) return [];
  const rank: Record<string, number> = { overdue: 0, at_risk: 1, not_due: 2 };
  return o.items
    .filter((i) => i.state !== 'met')
    .sort((a, b) => (rank[a.state] ?? 3) - (rank[b.state] ?? 3) || String(a.dueAt ?? '').localeCompare(String(b.dueAt ?? '')));
}

export async function fetchMyObligations(): Promise<EmployeeObligations | null> {
  const { status, body } = await httpRequestStatus<{ data: EmployeeObligations }>('/api/obligations/me', { endpointType: 'plain', retries: 0 });
  if (status === 204 || !body) return null;
  if (status >= 400) throw new Error(`obligations/me ha risposto ${status}`);
  return body.data;
}

export async function confirmObligation(kind: ObligationKind, periodFrom: string): Promise<void> {
  await httpRequest('/api/obligations/confirm', { method: 'POST', endpointType: 'plain', body: { kind, periodFrom } });
}

export async function unconfirmObligation(kind: ObligationKind, periodFrom: string): Promise<void> {
  const q = new URLSearchParams({ kind, periodFrom });
  await httpRequest(`/api/obligations/confirm?${q.toString()}`, { method: 'DELETE', endpointType: 'plain' });
}

export interface TeamObligationsRow {
  employee: { id: string; firstName?: string; lastName?: string; businessGroupId?: string | null };
  obligations: EmployeeObligations;
}

export async function fetchTeamObligations(params: {
  state?: ObligationState; kind?: ObligationKind; manager?: string; businessGroup?: string;
}): Promise<{ data: TeamObligationsRow[]; meta?: ObligationCounts }> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  return httpRequest(`/api/obligations${q.size ? `?${q.toString()}` : ''}`, { endpointType: 'plain' });
}
